import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchResumeData } from '../../../fetchProjects';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CATEGORY_STYLE = {
  Outreach:    'bg-accent/10 border-accent/20 text-accent-light',
  Innovation:  'bg-green-500/10 border-green-500/20 text-green-400',
  Research:    'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
  Recognition: 'bg-red-500/10 border-red-500/20 text-red-400',
  Hackathon:   'bg-purple-500/10 border-purple-500/20 text-purple-400',
  Training:    'bg-orange-500/10 border-orange-500/20 text-orange-400',
};

const toSlug = (item) => {
  const s = (item.title || '').toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 55).replace(/-+$/, '');
  return `${item.id}-${s}`;
};

const NotableAchievements = () => {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAllAch, setShowAllAch]   = useState(false);
  const [showAllOut, setShowAllOut]   = useState(false);
  const [showAllSpeak, setShowAllSpeak] = useState(false);
  const achRef   = useRef(null);
  const outRef   = useRef(null);
  const speakRef = useRef(null);
  const SHOW = 4;

  useEffect(() => {
    fetchResumeData().then(d => {
      setData(d.filter(i => ['achievement', 'speaking', 'outreach'].includes(i.type)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const achievements = data.filter(i => i.type === 'achievement');
  const outreach     = data.filter(i => i.type === 'outreach');
  const speaking     = data.filter(i => i.type === 'speaking');

  const handleToggle = (showAll, setShowAll, ref) => {
    if (showAll && ref.current) {
      window.scrollTo({ top: ref.current.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
    }
    setShowAll(s => !s);
  };

  const parseImages = (raw) => {
    try { const a = JSON.parse(raw || '[]'); return Array.isArray(a) ? a.filter(Boolean) : []; }
    catch { return raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []; }
  };

  if (!loading && data.length === 0) return null;

  const AchievementCard = ({ item }) => {
    const imgs = parseImages(item.images);
    const textBadge = item.slug ? item.slug.split('___')[0] : '';
    const catStyle = CATEGORY_STYLE[textBadge] || 'bg-white/5 border-white/10 text-text-muted';
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-accent/30 transition-smooth group flex flex-col h-full">
        {/* Cover Image */}
        {imgs[0] && (
          <div className="relative h-48 w-full overflow-hidden bg-black/40 flex items-center justify-center">
            {/* Blurred background to fill space */}
            <Image src={imgs[0]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 blur-xl scale-110" />
            {/* Main image fits exactly without cutting anything */}
            <Image src={imgs[0]} alt={item.title} className="relative z-10 w-full h-full object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" />
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          {/* Top Line: Category Badge */}
          {textBadge && (
            <div className="mb-2.5">
              <span className={`text-[11px] border px-2.5 py-0.5 rounded-full font-bold tracking-wider uppercase ${catStyle}`}>
                {textBadge}
              </span>
            </div>
          )}

          {/* Title */}
          <h3 className="font-heading font-black text-lg text-text-primary leading-tight line-clamp-2 md:min-h-[3rem] mb-3">
            {item.title}
          </h3>

          {/* Org & Logo Row */}
          <div className="flex items-center gap-2.5 mb-3">
            {item.badge && (
              <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center p-1 overflow-hidden flex-shrink-0 shadow-sm border border-white/20">
                <Image src={item.badge} alt={item.organization || item.title} className="w-full h-full object-contain" />
              </div>
            )}
            <div className="flex-1 min-w-0">
               {item.organization && <p className="text-accent font-semibold text-sm leading-tight line-clamp-1">{item.organization}</p>}
               {item.date_value && <p className="text-text-muted text-[11px] mt-0.5 font-medium tracking-wide uppercase">{item.date_value}</p>}
            </div>
          </div>

          {item.description && (
            <p className="text-text-secondary text-sm leading-relaxed line-clamp-2 mb-4 flex-1">{item.description}</p>
          )}
          {!item.description && <div className="flex-1" />}

          {imgs.length > 1 && (
            <div className="flex -space-x-2 mb-4 pt-2">
              {imgs.slice(0, 3).map((img, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-background overflow-hidden shadow-sm">
                  <Image src={img} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              {imgs.length > 3 && <div className="w-8 h-8 rounded-full border-2 border-background bg-white/10 flex items-center justify-center text-xs text-text-muted">+{imgs.length - 3}</div>}
            </div>
          )}
          <div className="pt-4 border-t border-white/5 mt-auto">
            <Link to={`/award/${toSlug(item)}`}
              className="inline-flex items-center gap-2 text-sm font-bold text-accent hover:text-accent-light transition-smooth group/link">
              Read More
              <Icon name="ArrowRight" size={16} className="group-hover/link:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const OutreachCard = ({ item }) => {
    const imgs = parseImages(item.images);
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-accent/30 transition-smooth group flex flex-col h-full shadow-lg">
        <div className="flex items-start gap-4 mb-3">
          {item.badge && (
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-1.5 overflow-hidden flex-shrink-0 shadow-sm border border-white/20">
              <Image src={item.badge} alt={item.organization || item.title} className="w-full h-full object-contain" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-heading font-bold text-text-primary text-base leading-snug mb-1 line-clamp-2">{item.title}</h4>
            {item.organization && <p className="text-accent text-sm font-semibold">{item.organization}</p>}
            {item.date_value && <p className="text-text-muted text-xs font-medium tracking-wide uppercase mt-0.5">{item.date_value}</p>}
          </div>
        </div>
        {item.description && (
          <p className="text-text-secondary text-sm leading-relaxed mt-2 line-clamp-2 flex-1">{item.description}</p>
        )}
        {!item.description && <div className="flex-1" />}
        <div className="mt-4 pt-3 border-t border-white/5">
          <Link to={`/award/${toSlug(item)}`}
            className="inline-flex items-center gap-2 text-sm font-bold text-accent hover:text-accent-light transition-smooth group/link">
            Read More <Icon name="ArrowRight" size={16} className="group-hover/link:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    );
  };

  const visAch   = showAllAch ? achievements : achievements.slice(0, SHOW);
  const visOut   = showAllOut ? outreach     : outreach.slice(0, SHOW);
  const visSpeak = showAllSpeak ? speaking   : speaking.slice(0, SHOW);

  return (
    <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30" />

      <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-6 relative z-10">
        Awards &amp; Honors
      </h2>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4 animate-pulse">
          {[1,2,3,4].map(i => <div key={i} className="h-40 bg-white/5 rounded-xl border border-white/10" />)}
        </div>
      ) : (
        <>
          {/* Achievements */}
          {achievements.length > 0 && (
            <div ref={achRef} className="mb-8 relative z-10">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">Awards &amp; Recognition</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {visAch.map(item => <AchievementCard key={item.id} item={item} />)}
              </div>
              {achievements.length > SHOW && (
                <div className="mt-6 flex justify-center">
                  <button onClick={() => handleToggle(showAllAch, setShowAllAch, achRef)}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent text-sm font-semibold transition-all">
                    {showAllAch ? 'Show Less' : `View All (${achievements.length} Total)`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Outreach */}
          {outreach.length > 0 && (
            <div ref={outRef} className="relative z-10 pt-8 border-t border-white/10 mt-8">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">Outreach Activities</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {visOut.map(item => <OutreachCard key={item.id} item={item} />)}
              </div>
              {outreach.length > SHOW && (
                <div className="mt-6 flex justify-center">
                  <button onClick={() => handleToggle(showAllOut, setShowAllOut, outRef)}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent text-sm font-semibold transition-all">
                    {showAllOut ? 'Show Less' : `View All (${outreach.length} Total)`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Speaking */}
          {speaking.length > 0 && (
            <div ref={speakRef} className="relative z-10 pt-8 border-t border-white/10 mt-8">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">Speaking Engagements</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {visSpeak.map(item => <OutreachCard key={item.id} item={item} />)}
              </div>
              {speaking.length > SHOW && (
                <div className="mt-6 flex justify-center">
                  <button onClick={() => handleToggle(showAllSpeak, setShowAllSpeak, speakRef)}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent text-sm font-semibold transition-all">
                    {showAllSpeak ? 'Show Less' : `View All (${speaking.length} Total)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default NotableAchievements;
